/**
 * The Agent Operating Procedure canvas.
 *
 * It draws a plan — a flat, ordered list of steps whose graph is implied by
 * their exits — and writes edits straight back as a new plan. Positions are
 * derived on every load (AOPLayoutUtils) and never persisted, so a drag is a
 * local nudge; the document has no coordinates to disagree about.
 *
 * Validation is the service's: every draft read and write returns `findings`,
 * and publish is refused while an error remains. Nothing is re-checked here.
 */

import { ExpandOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import {
	Background,
	BackgroundVariant,
	ConnectionLineType,
	Panel,
	ReactFlow,
	SelectionMode,
	useEdgesState,
	useNodesState,
	useReactFlow,
	useStore,
} from '@xyflow/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	AOPDraft,
	AOPDraftPlan,
	AOPDraftStep,
	AOPScenario,
	AOPStepType,
	AOP_MAX_STEPS,
	AOP_ON_EXHAUSTED,
	AOP_ON_TIMEOUT,
} from '../../types/AOPTypes';
import { AOPEditorApi, AOPEditorContext } from './AOPEditorContext';
import { AOPPalette, AOP_PALETTE_DRAG_TYPE } from './AOPPalette';
import { AOPStepsPanel } from './AOPStepsPanel';
import {
	AOPSuggestionNote,
	AOPViolationNote,
	unanchoredSuggestions,
	unanchoredViolations,
} from './AOPNotes';
import { AOPStartNode } from './nodes/AOPStartNode';
import { AOPRoutedEdge } from './nodes/AOPRoutedEdge';
import { AOPStepNode } from './nodes/AOPStepNode';
import AOPBlockPanel from './panel/AOPBlockPanel';
import {
	AOPNodeSizes,
	AOP_ROUTED_EDGE,
	AOP_START_NODE_ID,
	buildCanvasModel,
} from './utils/AOPLayoutUtils';
import { AOP_PANEL_MIN_WIDTH, AOP_STEPS_MIN_WIDTH, useResizablePanel } from './useResizablePanel';
import { createStepOfType, makeStepName, stepHasExits } from './utils/AOPPlanUtils';
import '@xyflow/react/dist/style.css';
import './AOPBuilder.less';

const nodeTypes = { aopStep: AOPStepNode, aopStart: AOPStartNode };
/*
  Declared out here, not inline: React Flow remounts every edge when the map
  identity changes, and an inline object is a new one on every render.
*/
const edgeTypes = { [AOP_ROUTED_EDGE]: AOPRoutedEdge };

/** What a block's panel opens at, before anyone drags the edge. */
const AOP_PANEL_OPEN_WIDTH = 600;

/** One control in the zoom cluster — the same button three times over. */
const ZOOM_BUTTON =
	'inline-flex h-[22px] w-[22px] cursor-pointer items-center justify-center rounded-sm ' +
	'border-0 bg-transparent text-grey-8 hover:bg-grey-3 hover:text-grey-10';

/*
 * React Flow's Background takes a colour value, not a class, so this is the one
 * place a token has to be written out. It is grey-5 from `tailwind.config.js`.
 */
const CANVAS_DOT_COLOR = '#d9d9d9';

interface AOPBuilderCanvasProps {
	draft: AOPDraft;
	onChangePlan: (plan: AOPDraftPlan) => void;
	/** Rules on one of the compiler's filled-in details. */
	onAcceptSuggestion: (suggestionId: string) => void;
	/**
	 * Showing a published version rather than the draft. Versions are immutable,
	 * so every write path is closed: no palette, no rewiring, no inline editors.
	 * Editing one means copying it into the draft first.
	 */
	isReadOnly?: boolean;
	/** The scenario routing work orders here, drawn as the Start block. */
	scenario: AOPScenario | null;
	/** Where the Start block sends an author to change those criteria. */
	scenariosHref: string;
	/**
	 * A block to open and centre on — set when somebody has been sent here from
	 * somewhere else, such as the list of what is holding publish up. Cleared
	 * once acted on, so asking for the same block twice works.
	 */
	focusStepId?: string | null;
	onFocusHandled?: () => void;
}

export const AOPBuilderCanvas: React.FC<AOPBuilderCanvasProps> = ({
	draft,
	onChangePlan,
	onAcceptSuggestion,
	isReadOnly,
	scenario,
	scenariosHref,
	focusStepId,
	onFocusHandled,
}) => {
	const reactFlow = useReactFlow();
	const zoom = useStore((state: any) => state.transform[2]);
	const plan: AOPDraftPlan = draft.plan ? draft.plan : { steps: [] };
	// What each card actually measures. Layout needs a size per node to place the
	// next column, and the plan carries none — so the first pass uses fallbacks
	// and every pass after it uses what React Flow measured. It settles after one
	// extra pass, because a card's size does not depend on where it sits.
	const [sizes, setSizes] = useState<AOPNodeSizes>({});
	const model = useMemo(
		() => buildCanvasModel(plan, draft.suggestions, draft.violations, scenario, sizes),
		[plan, draft.suggestions, draft.violations, scenario, sizes]
	);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	// One width for the column on the right, whether it is holding the palette or
	// a block's panel. A selected block gets more room than the palette needs, so
	// opening one widens it the first time and keeps whatever it is set to after.
	const panel = useResizablePanel(AOP_PANEL_MIN_WIDTH);
	// The Steps list is the other resizable column. It grows the other way, and
	// is clamped against the same measured canvas.
	const stepsPanel = useResizablePanel(340, {
		side: 'left',
		minWidth: AOP_STEPS_MIN_WIDTH,
	});

	// Sent here from elsewhere: open the block and bring it into view. React
	// Flow owns the viewport, so centring is a call rather than a layout change.
	useEffect(() => {
		if (!focusStepId) {
			return;
		}

		setSelectedId(focusStepId);
		panel.widenIfUntouched(AOP_PANEL_OPEN_WIDTH);

		const node = reactFlow.getNode(focusStepId);
		if (node) {
			const width = node.measured ? node.measured.width : 0;
			const height = node.measured ? node.measured.height : 0;
			reactFlow.setCenter(node.position.x + width / 2, node.position.y + height / 2, {
				zoom: Math.max(reactFlow.getZoom(), 1),
				duration: 300,
			});
		}

		if (onFocusHandled) {
			onFocusHandled();
		}
	}, [focusStepId, onFocusHandled, panel, reactFlow]);

	const selectNode = useCallback(
		(nodeId: string) => {
			setSelectedId(nodeId);
			panel.widenIfUntouched(AOP_PANEL_OPEN_WIDTH);
		},
		[panel]
	);
	// About the plan as a whole rather than any one step, so they float rather
	// than sitting on a card.
	const planSuggestions = useMemo(
		() => unanchoredSuggestions(draft.suggestions),
		[draft.suggestions]
	);
	const planViolations = useMemo(() => unanchoredViolations(draft.violations), [draft.violations]);

	const [nodes, setNodes, onNodesChange] = useNodesState(model.nodes as any);
	const [edges, setEdges] = useEdgesState(model.edges as any);

	// Push the plan into React Flow whenever it changes. React Flow insists on
	// owning nodes as state, so the document has to be synchronised into it; a
	// drag is a local nudge and is deliberately discarded on the next edit, since
	// layout is derived and positions are never persisted.
	useEffect(() => {
		setNodes(model.nodes as any);
		setEdges(model.edges as any);
	}, [model, setNodes, setEdges]);

	// Measuring is a read of the DOM React Flow owns, so it is a synchronisation
	// rather than derived state.
	//
	// Two things keep it from oscillating. Sub-pixel changes are ignored, or a
	// card whose height rounds differently between renders would re-lay-out for
	// ever. And a measurement is only ever added or updated, never dropped:
	// pushing a new plan in replaces the node objects with ones React Flow has
	// not measured yet, and forgetting their sizes there would send layout back
	// to the fallbacks, move every card, and start the whole cycle again.
	useEffect(() => {
		// Deferred by a frame: re-laying-out in the same frame as the measurement
		// that caused it is what makes a ResizeObserver report an undelivered
		// loop, which CRA's overlay then raises as a fatal error.
		const frame = window.requestAnimationFrame(() => {
			const next: AOPNodeSizes = { ...sizes };
			let changed = false;

			(nodes as any[]).forEach((node) => {
				if (!node.measured || !node.measured.width || !node.measured.height) {
					return;
				}

				next[node.id] = { width: node.measured.width, height: node.measured.height };
				const known = sizes[node.id];

				if (
					!known ||
					Math.abs(known.width - node.measured.width) > 1 ||
					Math.abs(known.height - node.measured.height) > 1
				) {
					changed = true;
				}
			});

			if (changed) {
				setSizes(next);
			}
		});

		return () => window.cancelAnimationFrame(frame);
	}, [nodes, sizes]);

	const selectedStep =
		selectedId === null || selectedId === AOP_START_NODE_ID
			? null
			: plan.steps.filter((step) => step.name === selectedId)[0];

	// A step the panel was showing can disappear — removed here, or gone from a
	// draft the service rewrote. Closing the panel is the honest response.
	useEffect(() => {
		if (selectedId !== null && selectedId !== AOP_START_NODE_ID && !selectedStep) {
			setSelectedId(null);
		}
	}, [selectedId, selectedStep]);

	/* ---------------------------- plan edit helpers --------------------------- */

	const writeSteps = useCallback(
		(steps: AOPDraftStep[]) => onChangePlan({ steps }),
		[onChangePlan]
	);

	const editor: AOPEditorApi = useMemo(
		() => ({
			isReadOnly: isReadOnly === true,
			updateStep: (stepId, patch) =>
				writeSteps(
					plan.steps.map((step) =>
						step.name === stepId ? ({ ...step, ...patch } as AOPDraftStep) : step
					)
				),
			replaceStep: (stepId, next) =>
				writeSteps(
					plan.steps.map((step) =>
						// The replacement takes the name, not the old step's fields: an
						// unsupported step carries `request`, `reason` and `alternatives`,
						// and a shape that does not declare them rejects the whole plan.
						step.name === stepId ? ({ ...next, name: stepId } as AOPDraftStep) : step
					)
				),
			removeStep: (stepId) => {
				// A plan holds one step at minimum upstream, so emptying it is a write
				// the service always refuses — the author would meet an error on an
				// action that looked ordinary. Refused here instead, as the create
				// drawer already does.
				if (plan.steps.length <= 1) {
					return;
				}

				/*
				  By position, not by name.

				  A name identifies a step well enough to find it — both ends keep
				  them unique, so `duplicate_step` is a rule neither side can reach
				  — but it is not what is being removed. One card is, and a filter
				  over names describes a set rather than the card that was clicked.
				  Resolving to the position says the thing meant, and stops being
				  right by coincidence if a name ever repeats.
				*/
				const at = plan.steps.findIndex((step) => step.name === stepId);

				if (at < 0) {
					return;
				}

				// Exits that pointed here are left pointing at a step that no longer
				// exists. The service reports each as a dangling_exit violation, which
				// is a clearer prompt to rewire than a branch silently retargeted
				// somewhere else.
				writeSteps(plan.steps.filter((step, position) => position !== at));
			},
		}),
		[isReadOnly, plan.steps, writeSteps]
	);

	const removeEscalation = useCallback(
		(stepId: string, index: number) =>
			writeSteps(
				plan.steps.map((step) => {
					if (step.name !== stepId || step.type !== 'decision') {
						return step;
					}

					const escalations = step.escalations ? step.escalations : [];
					const left = escalations.filter((other, position) => position !== index);

					/*
					  The service ties the catch-all exit's name to whether the step has
					  escalations, so taking the last one away renames that branch too.
					  Leaving it as `on_exhausted` with nothing to exhaust is a plan it
					  rejects, and the author would meet it as a publish error on a step
					  they only deleted a reminder from.
					*/
					if (left.length > 0) {
						return { ...step, escalations: left };
					}

					return {
						...step,
						escalations: left,
						exits: step.exits.map((exit) =>
							exit.name === AOP_ON_EXHAUSTED ? { ...exit, name: AOP_ON_TIMEOUT } : exit
						),
					};
				})
			),
		[plan.steps, writeSteps]
	);

	const addStep = useCallback(
		(stepType: AOPStepType) => {
			if (isReadOnly || plan.steps.length >= AOP_MAX_STEPS) {
				return;
			}

			const taken = new Set(plan.steps.map((step) => step.name));
			const id = makeStepName(stepType, taken);
			const finish = plan.steps.filter((step) => step.type === 'finish')[0];
			const fallback = finish ? finish.name : plan.steps.length > 0 ? plan.steps[0].name : id;

			writeSteps(plan.steps.concat([createStepOfType(stepType, id, fallback)]));
		},
		[isReadOnly, plan.steps, writeSteps]
	);

	const onConnect = useCallback(
		(connection: any) => {
			if (isReadOnly || !connection.source || !connection.target || !connection.sourceHandle) {
				return;
			}

			if (connection.source === connection.target) {
				return;
			}

			/*
			 * Every connection is a retarget, and only that.
			 *
			 * A communicate step carries one exit, `after`, so dragging its dot
			 * rewrites that exit like any other — onto a block that runs earlier
			 * included, which is a loop the plan can finally express.
			 *
			 * It used to be a reorder of the step list, because order was the flow
			 * and moving a block was the only way to say where a communicate step
			 * went. That cost three relationships per drag and could not go
			 * backwards at all. None of it is here any more.
			 */
			writeSteps(
				plan.steps.map((step) => {
					if (step.name !== connection.source || !stepHasExits(step)) {
						return step;
					}

					return {
						...step,
						exits: (step as any).exits.map((exit: any) =>
							exit.name === connection.sourceHandle ? { ...exit, target: connection.target } : exit
						),
					} as AOPDraftStep;
				})
			);
		},
		[isReadOnly, plan.steps, writeSteps]
	);

	// The retry cycles a plan contains are no longer drawn as a box behind the
	// blocks: a dashed edge means "points back at a step already passed" and
	// nothing else, so the cycle is legible from the edge alone, and the box was
	// another purple surface competing with the cards inside it.

	/* --------------------------------- chrome -------------------------------- */

	const onDrop = useCallback(
		(event: React.DragEvent) => {
			event.preventDefault();
			const stepType = event.dataTransfer.getData(AOP_PALETTE_DRAG_TYPE);

			if (stepType) {
				// Where it lands is decided by layout, not the pointer — the plan has
				// no coordinates to honour a drop position with.
				addStep(stepType as AOPStepType);
			}
		},
		[addStep]
	);

	return (
		<AOPEditorContext.Provider value={editor}>
			<div className="flex h-full flex-col bg-grey-1">
				<div
					className="relative flex min-h-0 flex-1"
					// Both columns clamp themselves against this element's width.
					ref={(node) => {
						panel.containerRef(node);
						stepsPanel.containerRef(node);
					}}
				>
					<div
						className={`aop-canvas ${isReadOnly ? 'aop-canvas-version' : ''}`}
						onDrop={onDrop}
						onDragOver={(event) => event.preventDefault()}
					>
						<ReactFlow
							nodes={nodes as any}
							edges={edges as any}
							onNodesChange={onNodesChange}
							onConnect={onConnect}
							nodeTypes={nodeTypes as any}
							edgeTypes={edgeTypes as any}
							nodesDraggable={!isReadOnly}
							nodesConnectable={!isReadOnly}
							onNodeClick={(_event, node: any) => selectNode(node.id)}
							onPaneClick={() => setSelectedId(null)}
							connectionLineType={ConnectionLineType.SmoothStep}
							defaultEdgeOptions={{ type: 'smoothstep' }}
							panOnScroll
							panOnDrag={[1, 2]}
							selectionOnDrag
							selectionMode={SelectionMode.Partial}
							fitView
							fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
							minZoom={0.2}
							proOptions={{ hideAttribution: true }}
							// Blocks are removed through the confirmed button on the card;
							// a stray keypress should not drop a step out of the plan.
							deleteKeyCode={null}
							edgesReconnectable={false}
						>
							{/* Dots on the draft only — see `.aop-canvas` for why. */}
							{isReadOnly ? null : (
								<Background
									variant={BackgroundVariant.Dots}
									gap={22}
									size={1}
									color={CANVAS_DOT_COLOR}
								/>
							)}

							{/*
							  One column rather than two panels. The Steps list and the zoom
							  cluster both live down the left, and as separate panels the
							  list had to guess how much room to leave for the cluster — it
							  guessed wrong, and the last step sat underneath it. Stacked in
							  a full-height column the list takes what is left and the
							  cluster keeps the bottom, whatever either of them measures.
							*/}
							<Panel position="top-left" className="aop-left-column">
								<div className="flex h-full min-h-0 flex-col items-start justify-between gap-s">
									<AOPStepsPanel
										width={stepsPanel.width}
										onHandlePointerDown={stepsPanel.onHandlePointerDown}
										onHandleKeyDown={stepsPanel.onHandleKeyDown}
										plan={plan}
										suggestions={draft.suggestions}
										violations={draft.violations}
										onRemoveStep={editor.removeStep}
										onRemoveEscalation={removeEscalation}
										isReadOnly={isReadOnly}
									/>

									<div className="footnoteregular inline-flex items-center gap-2xs rounded border border-solid border-grey-4 bg-grey-1 px-xs py-2xs text-grey-8 shadow-md">
										<button
											type="button"
											aria-label="Zoom out"
											className={ZOOM_BUTTON}
											onClick={() => reactFlow.zoomOut()}
										>
											<MinusOutlined />
										</button>
										<span className="footnoteregular text-character-secondary">
											{Math.round((zoom ? zoom : 1) * 100)}%
										</span>
										<button
											type="button"
											aria-label="Zoom in"
											className={ZOOM_BUTTON}
											onClick={() => reactFlow.zoomIn()}
										>
											<PlusOutlined />
										</button>
										<button
											type="button"
											aria-label="Fit view"
											className={ZOOM_BUTTON}
											onClick={() => reactFlow.fitView({ padding: 0.2, maxZoom: 1 })}
										>
											<ExpandOutlined />
										</button>
									</div>
								</div>
							</Panel>

							{planSuggestions.length + planViolations.length > 0 ? (
								<Panel position="top-right">
									<div className="flex w-[300px] max-w-[32vw] flex-col gap-2xs">
										{/*
										  Only findings about the plan as a whole live here. Every
										  other one is written to be read beside its step — "Nothing
										  reaches this step." — so it is rendered on the block itself.
										*/}
										{planViolations.map((violation, index) => (
											<AOPViolationNote key={index} violation={violation} />
										))}
										{planSuggestions.map((suggestion) => (
											<AOPSuggestionNote
												key={suggestion.id}
												suggestion={suggestion}
												onAccept={isReadOnly ? undefined : () => onAcceptSuggestion(suggestion.id)}
											/>
										))}
									</div>
								</Panel>
							) : null}
						</ReactFlow>
					</div>

					{isReadOnly || selectedId !== null ? null : (
						<AOPPalette
							onAdd={addStep}
							width={panel.width}
							onResizePointerDown={panel.onHandlePointerDown}
							onResizeKeyDown={panel.onHandleKeyDown}
						/>
					)}

					{selectedId === null ? null : (
						<AOPBlockPanel
							key={selectedId}
							step={selectedStep ? selectedStep : null}
							steps={plan.steps}
							suggestions={draft.suggestions}
							violations={draft.violations}
							onAcceptSuggestion={onAcceptSuggestion}
							isStart={selectedId === AOP_START_NODE_ID}
							isReadOnly={isReadOnly === true}
							scenario={scenario}
							scenariosHref={scenariosHref}
							onChange={(patch) => {
								if (selectedStep) {
									editor.updateStep(selectedStep.name, patch);
								}
							}}
							onReplaceStep={(next) => {
								if (selectedStep) {
									editor.replaceStep(selectedStep.name, next);
								}
							}}
							onRemove={() => {
								if (selectedStep) {
									editor.removeStep(selectedStep.name);
								}
							}}
							width={panel.width}
							onResizePointerDown={panel.onHandlePointerDown}
							onResizeKeyDown={panel.onHandleKeyDown}
							onClose={() => {
								setSelectedId(null);
								// React Flow keeps its own selection; without this the card
								// stays outlined after the panel it opened has gone.
								setNodes((current: any) =>
									(current as any[]).map((node) =>
										node.selected ? { ...node, selected: false } : node
									)
								);
							}}
						/>
					)}
				</div>
			</div>
		</AOPEditorContext.Provider>
	);
};

export default AOPBuilderCanvas;
